import type { ComponentProps } from "react";
import { Platform, Text } from "react-native";

import { useTranslation } from "@luna-restaurant/i18n";

import { ThemedText } from "./ThemedText";

export const RequiredMark = () => <Text aria-hidden>*</Text>;

export const useRequiredLabel = (): ((label: string) => string) => {
  const { t } = useTranslation();
  return (label) => `${label}, ${t("common.required")}`;
};

export const useRequiredControlProps = () => {
  const requiredLabel = useRequiredLabel();
  return (label: string, announced: boolean) =>
    !announced
      ? { accessibilityLabel: label }
      : Platform.OS === "web"
        ? { accessibilityLabel: label, "aria-required": true }
        : { accessibilityLabel: requiredLabel(label) };
};

type LabelTextProps = Omit<ComponentProps<typeof ThemedText>, "children"> & {
  children: string;
  required?: boolean;
  disabled?: boolean;
};

export const LabelText = ({
  children,
  required = false,
  disabled = false,
  ...rest
}: LabelTextProps) => {
  const requiredLabel = useRequiredLabel();
  const announced = required && !disabled && Platform.OS !== "web";

  return (
    <ThemedText
      {...rest}
      aria-label={announced ? requiredLabel(children) : undefined}
    >
      {children}
      {required ? <RequiredMark /> : null}
    </ThemedText>
  );
};
